import { User } from '../services/authService'

// Permission constants for type safety and avoiding typos
export const PERMISSIONS = {
  // Data access
  READ_OWN_DATA: 'read:own_data',
  WRITE_OWN_DATA: 'write:own_data',
  READ_ORG_DATA: 'read:org_data',
  WRITE_ORG_DATA: 'write:org_data',
  READ_CUSTOMER_DATA: 'read:customer_data',
  READ_ALL: 'read:all',
  WRITE_ALL: 'write:all',

  // User management
  MANAGE_USERS: 'manage:users',
  MANAGE_ORGANIZATION_USERS: 'manage:organization_users',
  MANAGE_SYSTEM: 'manage:system',

  // Organization management
  MANAGE_ORGANIZATION_SETTINGS: 'manage:organization_settings',
  MANAGE_ORGANIZATION_BILLING: 'manage:organization_billing',

  // Billing
  READ_BILLING: 'read:billing',

  // Support
  ACCESS_SUPPORT: 'access:support'
} as const

// Role ID constants for cleaner comparisons
export const ROLE_IDS = {
  // New role names
  ORGANIZATION_MEMBER: 1,
  ORGANIZATION_OWNER: 2,
  SYSTEM_ADMIN: 3,
  SUPPORT: 4,
  ORGANIZATION_ADMIN: 5,
  // Backward compatibility aliases
  USER: 1,
  ADMIN: 2
} as const

/**
 * Check if user has a specific permission
 */
export const hasPermission = (
  user: User | null,
  permission: string
): boolean => {
  if (!user || !user.permissions) return false
  return user.permissions.includes(permission)
}

/**
 * Check if user has any of the specified permissions
 */
export const hasAnyPermission = (
  user: User | null,
  permissions: string[]
): boolean => {
  if (!user || !user.permissions) return false
  return permissions.some((permission) =>
    user.permissions!.includes(permission)
  )
}

/**
 * Check if user has all of the specified permissions
 */
export const hasAllPermissions = (
  user: User | null,
  permissions: string[]
): boolean => {
  if (!user || !user.permissions) return false
  return permissions.every((permission) =>
    user.permissions!.includes(permission)
  )
}

/**
 * Check if user can access support features
 */
export const canAccessSupport = (user: User | null): boolean => {
  return hasAnyPermission(user, [
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.READ_CUSTOMER_DATA,
    PERMISSIONS.MANAGE_SYSTEM
  ])
}

/**
 * Check if user should see all customers or just their own
 */
export const canAccessAllCustomers = (user: User | null): boolean => {
  return hasAnyPermission(user, [
    PERMISSIONS.READ_ORG_DATA,
    PERMISSIONS.READ_CUSTOMER_DATA,
    PERMISSIONS.READ_ALL,
    PERMISSIONS.MANAGE_SYSTEM
  ])
}

/**
 * Check if user is a regular user (should auto-select their customer)
 */
export const isRegularUser = (user: User | null): boolean => {
  return user?.roleId === ROLE_IDS.USER
}

/**
 * Check if user is an Organization Owner
 */
export const isOrganizationOwner = (user: User | null): boolean => {
  return user?.roleId === ROLE_IDS.ORGANIZATION_OWNER
}

/**
 * Check if user is an Organization Admin
 */
export const isOrganizationAdmin = (user: User | null): boolean => {
  return user?.roleId === ROLE_IDS.ORGANIZATION_ADMIN
}

/**
 * Check if user is a Support
 */
export const hasSupportRole = (user: User | null): boolean => {
  return user?.roleId === ROLE_IDS.SUPPORT
}

/**
 * Check if user can manage organization users
 * Based on manage:organization_users permission
 */
export const canManageUsers = (user: User | null): boolean => {
  return hasPermission(user, PERMISSIONS.MANAGE_ORGANIZATION_USERS)
}

/**
 * Check if user can manage organization billing
 * Based on manage:organization_billing permission
 */
export const canManageOrganization = (user: User | null): boolean => {
  return hasPermission(user, PERMISSIONS.MANAGE_ORGANIZATION_BILLING)
}

/**
 * Check if user is System Admin
 */
export const isSystemAdmin = (user: User | null): boolean => {
  return user?.roleId === ROLE_IDS.SYSTEM_ADMIN
}

/**
 * Check if user can access Support page
 * Based on access:support permission
 */
export const canAccessSupportPage = (user: User | null): boolean => {
  return hasPermission(user, PERMISSIONS.ACCESS_SUPPORT)
}
